import { z } from 'zod';
import { statusToneSchema, timestampSchema, uuidSchema, versionedSchema } from './common';
import {
  ENTITY_CATALOG,
  ENTITY_DEFINITIONS,
  ENTITY_KINDS,
  entityDefinition,
  entityKindSchema,
  entityRefKey,
  entityRefSchema,
  readableKindsFor,
  sameEntityRef,
  type EntityDefinition,
  type EntityKind,
  type EntityRef,
} from './entities';
import type { Permission } from './permissions';

/**
 * Cross-references between items (docs/cross-references-design.md).
 *
 * The product holds several kinds of item that describe the same piece of the
 * real world from different angles: a track renewal is a schedule task, the
 * corridor it runs along is a map feature, the money for it sits under a cost
 * account. Until now the only way to say "these two rows are about the same
 * thing" was a foreign key written for that specific pair -- which is why
 * `map_feature` carries one column for services and another for tasks, each
 * with its own constraint, trigger and read-path branch.
 *
 * That price is defensible once. It is not defensible n^2 times, and meeting
 * minutes, changelogs and decision logs are all coming. So this is the general
 * mechanism: a link between two items of any two registered kinds, navigable
 * from both ends.
 *
 * Two ideas carry the whole design and are worth stating before the schemas:
 *
 *  1. **A link is stored between canonically ordered ends, and read from an
 *     anchor.** The storage form -- `a`, `b`, and which way the relation reads
 *     -- never leaves the service. Callers write `{ from, to, relation }` and
 *     read a list presented from the perspective of the item they asked about.
 *     That is what makes "one link per pair" enforceable by an ordinary unique
 *     constraint while still letting the relation mean something directional.
 *
 *  2. **Everything kind-specific is declared, not coded.** What a kind *is*
 *     lives in `entities.ts` -- the platform's catalog of its own kinds -- and
 *     nothing in this file switches on kind either.
 */

/* -------------------------------------------------------- the kinds, moved */

/**
 * The catalog used to live here, under `LINK_ENTITY_*` names, back when links
 * were the only mechanism that addressed items by kind. Four mechanisms do now
 * (`entities.ts`), so it moved and these are aliases.
 *
 * They are not deprecated in the "will be removed" sense so much as in the
 * "read the other name in new code" sense: `linkableKindsFor` says *linkable*,
 * and a comment thread asking it that question would be asking the wrong
 * question with the right answer.
 *
 * @deprecated Prefer the `ENTITY_*` names from `entities.ts`.
 */
export const LINK_ENTITY_KINDS = ENTITY_KINDS;
/** @deprecated Prefer `entityKindSchema`. */
export const linkEntityKindSchema = entityKindSchema;
/** @deprecated Prefer `EntityKind`. */
export type LinkEntityKind = EntityKind;
/** @deprecated Prefer `EntityDefinition`. */
export type LinkEntityDefinition = EntityDefinition;
/** @deprecated Prefer `ENTITY_CATALOG`. */
export const LINK_ENTITY_CATALOG = ENTITY_CATALOG;
/** @deprecated Prefer `ENTITY_DEFINITIONS`. */
export const LINK_ENTITY_DEFINITIONS = ENTITY_DEFINITIONS;
/** @deprecated Prefer `entityDefinition`. */
export const linkEntityDefinition = entityDefinition;
/** @deprecated Prefer `readableKindsFor`. */
export const linkableKindsFor = readableKindsFor;

/* ----------------------------------------------------------- the relations */

/**
 * How two items are related.
 *
 * A short closed list on purpose. Every member has to earn two translated
 * labels and a place in a dropdown that people read on the way to doing
 * something else; a taxonomy nobody can hold in their head produces links
 * classified at random, which is worse for finding things than no
 * classification at all.
 */
export const LINK_RELATIONS = [
  'relates_to',
  'located_at',
  'documents',
  'affects',
  'duplicates',
] as const;
export const linkRelationSchema = z.enum(LINK_RELATIONS);
export type LinkRelation = (typeof LINK_RELATIONS)[number];

/**
 * Relations that read the same from both ends.
 *
 * A symmetric relation still has a stored direction -- the row records which
 * end was authored first -- but both ends render the same label, so nothing
 * downstream has to care which one it was.
 */
export const SYMMETRIC_LINK_RELATIONS: ReadonlySet<LinkRelation> = new Set<LinkRelation>([
  'relates_to',
  'duplicates',
]);

export function isSymmetricRelation(relation: LinkRelation): boolean {
  return SYMMETRIC_LINK_RELATIONS.has(relation);
}

/**
 * The translation key for how a relation reads from one end.
 *
 * Asymmetric relations have two labels -- a task is "located at" a feature, and
 * the feature is the "location of" the task -- and getting the wrong half is
 * the difference between a sentence and a riddle. Symmetric ones resolve to the
 * forward key from both ends rather than needing a duplicate reverse entry in
 * every locale file.
 */
export function linkRelationLabelKey(
  relation: LinkRelation,
  direction: LinkDirectionFromAnchor,
): string {
  const side = direction === 'outgoing' || isSymmetricRelation(relation) ? 'forward' : 'reverse';
  return `links.relation.${relation}.${side}`;
}

/* -------------------------------------------------- canonical link storage */

/**
 * Which end of a stored link the relation reads from.
 *
 * Storage-side only: `a_to_b` means "a <relation> b". Callers never see this --
 * they see `LinkDirectionFromAnchor` below, which is relative to whatever they
 * asked about.
 */
export const LINK_DIRECTIONS = ['a_to_b', 'b_to_a'] as const;
export const linkDirectionSchema = z.enum(LINK_DIRECTIONS);
export type LinkDirection = (typeof LINK_DIRECTIONS)[number];

/** How a link reads from the item the caller asked about. `outgoing` means the
 *  anchor is the relation's subject. */
export const LINK_DIRECTIONS_FROM_ANCHOR = ['outgoing', 'incoming'] as const;
export const linkDirectionFromAnchorSchema = z.enum(LINK_DIRECTIONS_FROM_ANCHOR);
export type LinkDirectionFromAnchor = (typeof LINK_DIRECTIONS_FROM_ANCHOR)[number];

export interface CanonicalLink {
  a: EntityRef;
  b: EntityRef;
  direction: LinkDirection;
}

/**
 * Puts an authored link into storage order.
 *
 * The ends are sorted by `kind:id` so that "A links to B" and "B links to A"
 * become the same row, and the unique constraint on the four endpoint columns
 * can enforce one link per pair without an application-level check that two
 * concurrent writers could slip between. `direction` carries the authored
 * reading, so nothing is lost by the sort.
 */
export function canonicalizeLink(from: EntityRef, to: EntityRef): CanonicalLink {
  return entityRefKey(from) <= entityRefKey(to)
    ? { a: from, b: to, direction: 'a_to_b' }
    : { a: to, b: from, direction: 'b_to_a' };
}

/**
 * Reads a stored link back from one end.
 *
 * Returns the far end and how the relation reads from the near one. The anchor
 * must be one of the two ends; a caller that passes anything else has asked a
 * question about a link that is not theirs, which is a programming error rather
 * than a runtime condition.
 */
export function orientLink(
  link: CanonicalLink,
  anchor: EntityRef,
): { target: EntityRef; direction: LinkDirectionFromAnchor } {
  if (sameEntityRef(link.a, anchor)) {
    return { target: link.b, direction: link.direction === 'a_to_b' ? 'outgoing' : 'incoming' };
  }
  if (sameEntityRef(link.b, anchor)) {
    return { target: link.a, direction: link.direction === 'b_to_a' ? 'outgoing' : 'incoming' };
  }
  throw new Error(`${entityRefKey(anchor)} is not an end of this link`);
}

/* ------------------------------------------------------- authorization */

/**
 * Whether a caller may create, retype or remove a link between two items.
 *
 * Read on both ends, manage on at least one. Requiring manage on both would
 * mean that saying "this task happens in this place" needed the union of the
 * scheduling and mapping editing rights, which in practice is an administrator
 * -- and a cross-reference nobody can create is a feature nobody has.
 *
 * The read half is not a formality: without it, linking would be a way to
 * discover the names of items you are not allowed to see, because the link you
 * created would come straight back resolved.
 */
export function canWriteLink(
  permissions: readonly Permission[],
  from: EntityRef,
  to: EntityRef,
): boolean {
  const held = new Set(permissions);
  const ends = [linkEntityDefinition(from.kind), linkEntityDefinition(to.kind)];
  if (!ends.every((definition) => held.has(definition.readPermission))) return false;
  return ends.some((definition) => held.has(definition.managePermission));
}

/* ----------------------------------------------------------- wire: reading */

/**
 * Where a link was recorded.
 *
 * `explicit` is a row in `entity_link`, created by somebody. `derived` is a
 * relation a module already holds in its own columns -- the map's feature
 * subject -- surfaced here rather than copied here, so there is nothing to keep
 * in step. A derived link cannot be deleted through this API: it is unlinked
 * where it lives.
 */
export const LINK_ORIGINS = ['explicit', 'derived'] as const;
export const linkOriginSchema = z.enum(LINK_ORIGINS);
export type LinkOrigin = (typeof LINK_ORIGINS)[number];

/**
 * The far end of a link, resolved.
 *
 * Everything needed to recognise the item and go to it, and nothing more: the
 * list is a list of ways out of the screen, not a preview of each destination.
 * `statusCode` is a translation key rather than a sentence, because the backend
 * does not speak the reader's language (section 16).
 */
export const linkedEntitySchema = z.object({
  kind: linkEntityKindSchema,
  id: uuidSchema,
  label: z.string(),
  /**
   * Enough to tell two similarly named items apart: a human-authored key, a
   * code, a category. Null when the item has none.
   *
   * Literal text, not a translation key, and specifically the kind of literal
   * that does not need translating -- an identifier somebody typed, which reads
   * the same in every locale. Anything the backend would have to translate goes
   * in `statusCode` beside it (section 16).
   */
  secondary: z.string().nullable(),
  /** Where the rest of the application talks about this item. Built by the API
   *  so no consumer has to know how another module routes. */
  href: z.string(),
  statusTone: statusToneSchema,
  statusCode: z.string().nullable(),
  /** The project this item belongs to, or null for organization-scoped items.
   *  The link service uses it to refuse a cross-project link. */
  projectId: uuidSchema.nullable(),
});
export type LinkedEntity = z.infer<typeof linkedEntitySchema>;

export const entityLinkSchema = z.object({
  /** Null for a derived link: there is no row to address. */
  id: uuidSchema.nullable(),
  origin: linkOriginSchema,
  relation: linkRelationSchema,
  direction: linkDirectionFromAnchorSchema,
  target: linkedEntitySchema,
  note: z.string().nullable(),
  createdAt: timestampSchema.nullable(),
  version: z.number().int().nonnegative().nullable(),
});
export type EntityLink = z.infer<typeof entityLinkSchema>;

export const entityLinkListSchema = z.object({
  anchor: entityRefSchema,
  links: z.array(entityLinkSchema),
  /**
   * True when the anchor holds more links than the ceiling below. The list says
   * so rather than showing a partial picture as if it were the whole one.
   */
  truncated: z.boolean(),
});
export type EntityLinkList = z.infer<typeof entityLinkListSchema>;

/* ----------------------------------------------------------- wire: queries */

/**
 * The read ceiling, and the write ceiling with it.
 *
 * One number for both, so the list a reader sees is never a page of something
 * larger: an item cannot acquire a two-hundred-and-first link, so a list of two
 * hundred is complete by construction. Section 10.1 forbids unbounded payloads,
 * and an item with two hundred cross-references has a modelling problem that
 * pagination would only hide.
 */
export const MAX_LINKS_PER_ENTITY = 200;

export const entityLinkQuerySchema = z
  .object({ kind: linkEntityKindSchema, id: uuidSchema })
  .strict();
export type EntityLinkQuery = z.infer<typeof entityLinkQuerySchema>;

export const LINK_CANDIDATE_LIMIT_MAX = 25;

/**
 * The picker's query: which kind to search, and what was typed.
 *
 * `projectId` narrows project-scoped kinds to the project the anchor is in,
 * which is not decoration -- offering candidates that cannot legally be linked
 * is a list whose entries fail on click. `excludeKind`/`excludeId` drop the
 * anchor itself, since nothing links to itself.
 */
export const linkCandidateQuerySchema = z
  .object({
    kind: linkEntityKindSchema,
    q: z.string().max(120).optional(),
    projectId: uuidSchema.optional(),
    excludeKind: linkEntityKindSchema.optional(),
    excludeId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(LINK_CANDIDATE_LIMIT_MAX).default(10),
  })
  .strict();
export type LinkCandidateQuery = z.infer<typeof linkCandidateQuerySchema>;

export const linkCandidateListSchema = z.object({
  kind: linkEntityKindSchema,
  candidates: z.array(linkedEntitySchema),
  truncated: z.boolean(),
});
export type LinkCandidateList = z.infer<typeof linkCandidateListSchema>;

/* ---------------------------------------------------------- wire: mutation */

export const MAX_LINK_NOTE_LENGTH = 500;

/**
 * Creating a link.
 *
 * `from` and `to` are as the author means them to read, not as they will be
 * stored: the service sorts them and records which way round the relation goes.
 * That keeps the storage encoding out of the API, so a client never has to know
 * that "A documents B" and "B documents A" are the same row seen twice.
 */
export const createEntityLinkSchema = z
  .object({
    from: entityRefSchema,
    to: entityRefSchema,
    relation: linkRelationSchema.default('relates_to'),
    note: z.string().max(MAX_LINK_NOTE_LENGTH).nullable().optional(),
  })
  .strict()
  .refine((value) => !sameEntityRef(value.from, value.to), {
    message: 'an item cannot be linked to itself',
    path: ['to'] as PropertyKey[],
  });
export type CreateEntityLink = z.infer<typeof createEntityLinkSchema>;

/**
 * Editing a link: its relation and its note, never its ends.
 *
 * Re-pointing a link at a different item is not an edit, it is a different
 * link -- and allowing it would let one `PATCH` collide with the uniqueness of
 * a pair that the caller never named.
 */
export const updateEntityLinkSchema = z
  .object({
    relation: linkRelationSchema.optional(),
    note: z.string().max(MAX_LINK_NOTE_LENGTH).nullable().optional(),
    /**
     * Which end the relation reads from, expressed the way the caller sees the
     * link rather than the way it is stored. Sent together with a relation when
     * the editor offers "documents" and "documented by" as two choices.
     */
    direction: linkDirectionFromAnchorSchema.optional(),
    /** The anchor the caller is looking at, needed to interpret `direction`. */
    anchor: entityRefSchema.optional(),
  })
  .extend(versionedSchema.shape)
  .strict()
  .refine((value) => value.direction === undefined || value.anchor !== undefined, {
    message: 'direction may only be changed relative to an anchor',
    path: ['anchor'] as PropertyKey[],
  });
export type UpdateEntityLink = z.infer<typeof updateEntityLinkSchema>;
